import { useTranslation, untranslated } from "@i18n";
import type { ComponentProps } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { useState, useEffect, useMemo } from "react";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import AttachIssues from "./AttachIssues/AttachIssues";
import AttachFiles from "./AttachFiles/AttachFiles";
import { showToastNotification } from "@Reducers/toastSlice";
import { createFileShortcut, consumeExternalFiles } from "@Actions/fileUpload";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS, TOAST_TYPE_WARNING } from "@Services/constants";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import FileManager from "./FileManagerNew";
import { Box, CircularProgress } from "@mui/material";
import IconComponent from "@CubeComponents/icons";
import { getFileIcon } from "@CubeComponents/constants/globalFunctions";
import { FilesGrid } from "@Features/files";
import ToggleSwitchComponent from "@CubeComponents/form/ToggleSwitchComponent";
import SelectSpaces from "@Components/WorkspaceList/SelectSpaces";
import type { Space } from "@Components/WorkspaceList/useSelectSpaces";
import Text from "@CubeComponents/html/Text/Text";
import useFileManagerAsPopupShared from "./useFileManagerAsPopupShared";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { RootState } from "@Reducers/index";
import type { SelectedFileItem } from "@ServerTypes/folders/folders.schema";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";

/**
 * A file row as the attach-issues flow carries it: the canonical item plus the version fields that
 * flow decides duplicates on. No index signature — `SelectedFileItem` is Zod-derived, and adding one
 * would stop plain `SelectedFileItem[]` from satisfying callbacks typed against this.
 */
type IssueFileRow = SelectedFileItem & {
  fileVersionNumber?: number;
  issueId?: ObjectIdString;
  issueVersionId?: ObjectIdString;
  status?: string;
};

/** One row of the picker payload `FilesGrid` hands to its `handleFileSelectionCallback`. */
type FilesGridPickerRow = Parameters<
  NonNullable<ComponentProps<typeof FilesGrid>["handleFileSelectionCallback"]>
>[0][number];

const asString = (value: unknown): string | undefined => (typeof value === "string" ? value : undefined);
const asNumber = (value: unknown): number | undefined => (typeof value === "number" ? value : undefined);
const asBoolean = (value: unknown): boolean | undefined => (typeof value === "boolean" ? value : undefined);

/**
 * Narrow one `FilesGrid` picker row into the row shape this popup carries.
 *
 * `FilesGrid` declares every member of that payload `unknown`, and that is HONEST rather than lazy:
 * its `handleRowSelection` builds each row by reading `_id`, `spaceId`, `parentId`, `isFile` and
 * `engineType` off its own `FileData` — and `FileData` (FilesGrid.tsx, `type FileData`) declares
 * NONE of them. They arrive through its `[key: string]: unknown` index signature, so nothing
 * upstream has checked them and deriving the payload from `FileData` would hand back `unknown`
 * again. Narrowing here is therefore the only honest read. The row is spread through first so no
 * runtime member a caller reads is dropped; only the members this popup's own row type names are
 * re-stated.
 */
const toPopupFileRow = (row: FilesGridPickerRow): SelectedFileItem => ({
  ...row,
  id: objectIdString.safeParse(row.id).data,
  hierarchyId: objectIdString.safeParse(row.hierarchyId).data,
  spaceId: objectIdString.safeParse(row.spaceId).data,
  name: asString(row.name),
  parentId: asString(row.parentId),
  resourceType: asString(row.resourceType),
  conversionStatus: asString(row.conversionStatus),
  fileVersion: asString(row.fileVersion),
  engineType: asString(row.engineType),
  size: asNumber(row.size),
  versionNumber: asNumber(row.versionNumber),
  isContainer: asBoolean(row.isContainer),
  isFile: asBoolean(row.isFile) ?? asNumber(row.isFile),
});
/**
 * One entry of the `consumeExternalFiles` payload. The endpoint declares `fileVersionIds` as
 * `z.array(z.unknown())` because the entries are forwarded verbatim, so the shape this component
 * builds — and that the duplicates prompt rewrites — is stated here.
 */
type ConsumeFileVersion = { fileVersion?: string; uploadNewVersion?: boolean; uploadNewFile?: boolean };

/** The `consumeExternalFiles` request as this component builds it, for the current destination folder. */
type ConsumeFilesPayload = {
  fileVersionIds: ConsumeFileVersion[];
  spaceId: string;
  parentId: string;
  skipDuplicateCheck: boolean;
  transmittalData?: Record<string, unknown>;
};

interface FileManagerAsPopupProps {
  /**
   * METHOD syntax, not a property arrow, and that is deliberate: three unrelated callers supply this
   * (`Schedule/FileManagement`, `estimation/FilesTab`, and `FileManagerNew`'s merge/import handlers),
   * each declaring its own narrower payload shape. Method signatures are checked bivariantly even
   * under `strictFunctionTypes`, so all three remain assignable without an `any` or a cast at each
   * call site.
   */
  handleFileSelectedButtonClick?(selected: never, ...rest: never[]): unknown;
  /**
   * REQUIRED, not optional. This popup always renders its own "Cancel" button bound to this prop and
   * both external-file flows call it to close themselves once their request settles, so a caller that
   * omitted it would ship a dialog with no way out; all 22 call sites already pass one. Stating that
   * here is what lets this file call it directly instead of asserting it non-null at four sites.
   */
  handleFileSelectedCancelClick: () => void;
  handleFolderSelected?(folder: never, ...rest: never[]): unknown;
  allowFolderSelectionCallback?: boolean; // default: false
  /** `null` at some call sites (mailbox), not just absent. */
  workspaceId?: string | null;
  id?: string | null;
  openThisSpaceByDefault?: boolean; // default: false
  hideIssues?: boolean; // default: false
  hideTasks?: boolean; // default: false
  hideReviews?: boolean; // default: false
  hideEstimates?: boolean; // default: false
  hideExternalSpace?: boolean; // default: false
  hideClashTests?: boolean; // default: false
  createShortcut?: boolean; // default: false
  consumeFiles?: boolean; // default: false
  hideSchedules?: boolean; // default: false
  hideContracts?: boolean; // default: false
  hideMergeFiles?: boolean; // default: false
  enableOnlyShareableResources?: boolean; // default: false
  /**
   * Two shapes reach this prop, one per external-file flow — `SingleTransmittal` keeps both in one
   * state. The consume flow passes the selected reference ROWS, which `handleConsumeFiles` maps over;
   * the shortcut flow passes the `createFileShortcut` request itself, which `handleCreateShortcut`
   * reads. Declaring only the array shape is what forced an assertion at both ends of each flow.
   */
  shortCutData?:
    | { fileVersionId?: ObjectIdString; fileId?: ObjectIdString; fileVersion?: string }[]
    | { transmittalId?: string; fileVersionIds?: string[] }; // default: {}
  hideAllTabs?: boolean; // default: false
  useAttachedExternalVolume?: boolean; // default: false
  heading?: string | null;
  allowInput?: boolean;
  inputLabel?: string;
  isTransmittal?: boolean;
  externalFolderData?: Record<string, unknown>;
  /** Callers map version ids straight off rows, so entries can be `undefined`. */
  disableFiles?: (string | undefined)[];
  minimumFiles?: number;
  errorMessage?: string;
  showOnlyMergedFile?: boolean;
  addEmptyContainerToList?: boolean;
  enableFilesTypes?: string[] | null;
  hideShortcuts?: boolean; // default: false
  createMerge?: boolean; // default: false
  attachSelectedBtnName?: string | null; // default: null
  /**
   * `unknown[]` at the `questionFields` call site: it passes `currentAnswer.value`, which the forms
   * answer type declares as `unknown`. Widened rather than asserted at that caller, since the value
   * genuinely is unvalidated there.
   */
  selectedFilesData?: SelectedFileItem[] | unknown; // default: []
  disableMultipleSelection?: boolean;
  projectId?: string | null;
  enableSpaceListDropdown?: boolean;
  setSpaceId?: (id: ObjectIdString) => void;
  disableContextMenu?: boolean; // default: false
  setIsLoadingCreateButton?: (loading: boolean) => void;
  isLoadingCreateButton?: boolean;
  isLoadingAddFiles?: boolean;
  setIsLoadingAddFiles?: (loading: boolean) => void;
  allowSwitchSpace?: boolean;
  hideFilesWithoutVersion?: boolean;
  allowViewToggle?: boolean;
  // Constrained list for the header Space dropdown. When provided, the dropdown shows only these
  // spaces (instead of fetching every project space) and switching is enabled if there is >1 option.
  spaceListDataSource?: Space[] | null;
}
function FileManagerAsPopup({
  handleFileSelectedButtonClick,
  handleFileSelectedCancelClick,
  handleFolderSelected,
  allowFolderSelectionCallback = false,
  workspaceId,
  openThisSpaceByDefault = false,
  hideIssues = false,
  hideTasks = false,
  hideReviews = false,
  hideEstimates = false,
  hideExternalSpace = false,
  hideClashTests = false,
  createShortcut = false,
  consumeFiles = false,
  hideSchedules = false,
  hideContracts = false,
  hideMergeFiles = false,
  enableOnlyShareableResources = false,
  shortCutData = {},
  hideAllTabs = true,
  useAttachedExternalVolume = false,
  heading,
  allowInput,
  inputLabel,
  isTransmittal,
  externalFolderData,
  disableFiles,
  minimumFiles,
  errorMessage,
  showOnlyMergedFile,
  addEmptyContainerToList,
  enableFilesTypes,
  hideShortcuts = false,
  createMerge = false,
  attachSelectedBtnName = null,
  selectedFilesData = [],
  disableMultipleSelection,
  projectId,
  setSpaceId,
  disableContextMenu = false,
  setIsLoadingCreateButton,
  isLoadingCreateButton,
  isLoadingAddFiles,
  setIsLoadingAddFiles,
  allowSwitchSpace = false,
  allowViewToggle = true,
  hideFilesWithoutVersion = false,
  spaceListDataSource,
}: Readonly<FileManagerAsPopupProps>) {
  const { t } = useTranslation();
  const layout = useSelector((state: RootState) => state.layout);
  const CDEPLAN = layout.SPACES?.ENABLE;
  const PMPLAN = layout.FILES?.ENABLE;

  const dispatch = useDispatch();

  /**
   * `selectedFilesData` is declared `unknown` because the forms caller passes `currentAnswer.value`,
   * an unvalidated answer value; every other caller passes the file manager's own selection. Check
   * the shape once here instead of asserting it at each use — memoised because the shared hook has
   * this in an effect's dependency list.
   */
  const preSelectedFiles = useMemo<SelectedFileItem[]>(
    () => (Array.isArray(selectedFilesData) ? selectedFilesData : []),
    [selectedFilesData],
  );

  /**
   * Callers map version ids straight off rows, so `disableFiles` can carry `undefined` entries, while
   * `FilesGrid` declares `string[]` and only ever asks whether a row's version id is in the list —
   * dropping the empty entries is what that list means.
   */
  const disabledFileVersionIds = useMemo(
    () => disableFiles?.filter((fileVersionId): fileVersionId is string => fileVersionId !== undefined),
    [disableFiles],
  );
  // `heading` is caller-supplied copy, so it goes through `untranslated()` rather than `t()`.
  const fileManagerHeading = heading ? untranslated(heading) : t("Attach Files");

  // Use shared hook for file selection logic (platform-agnostic)
  const {
    listOfSelectedFiles,
    setListOfSelectedFiles,
    selectedFolderData,
    inputValue,
    setInputValue,
    issuesHavingNoFiles,
    AddToListDisabled,
    disableAttachButton,
    disableCreateShortcutButton,
    handleFileSelectionCallback,
    handleAddToSelection,
    handleRemoveOfSelectedFile,
    handleFileSelectedButtonClickCallback,
    handleFolderSelectionCallback,
    handleFolderSelectedCallback,
    removeIssuesWithNoFiles,
    acceptFilesForIssues,
    // View mode state
    toggleFilesAsList,
    handleToggleFilesAsList,
    showViewToggle,
    // Space switching state
    switchSpaceId,
    showNoSpaceWarning,
    setShowNoSpaceWarning,
    handleSpaceListFetch,
    handleSpaceSelect,
  } = useFileManagerAsPopupShared({
    // The hook declares these two with the same bivariant method syntax, so they pass straight
    // through. The payload is the selection MAP, not a file array — that is why neither is named.
    handleFileSelectedButtonClick,
    handleFolderSelected,
    minimumFiles,
    allowInput,
    disableMultipleSelection,
    isTransmittal,
    addEmptyContainerToList,
    createMerge,
    createShortcut,
    selectedFilesData: preSelectedFiles,
    setIsLoadingCreateButton,
    setIsLoadingAddFiles,
    // The prop is `null` at some call sites (mailbox) while the hook declares it optional; every
    // read of it in the hook is a truthiness check, so the two nullish values are interchangeable.
    workspaceId: workspaceId ?? undefined,
    hasCDEPlan: !!CDEPLAN,
    hasPMPlan: !!PMPLAN,
    allowViewToggle,
  });

  // Web-specific state
  const [openAttachIssuesModal, setOpenAttachIssuesModal] = useState(false);
  const [filesIssues] = useState([]);
  /**
   * Set when `consumeExternalFiles` reports duplicates at the destination: the payload to retry once
   * the user picks how to resolve them, plus the count the prompt shows. `null` while there is no
   * prompt — it used to be `false`, which is why every read of it needed an assertion.
   */
  const [duplicatePrompt, setDuplicatePrompt] = useState<{
    payload: ConsumeFilesPayload;
    duplicatesFound: number;
  } | null>(null);
  const [submitButtonLoading, setSubmitButtonLoading] = useState(false);

  // When a constrained space list is supplied, useSelectSpaces skips the project-spaces fetch, so the
  // fetch callback that normally clears the loading state never fires. Resolve the warning flag here
  // (mirrors handleSpaceListFetch: warn only when there is neither a workspace nor any space option).
  useEffect(() => {
    if (spaceListDataSource) {
      setShowNoSpaceWarning(!workspaceId && spaceListDataSource.length === 0);
    }
  }, [spaceListDataSource, workspaceId, setShowNoSpaceWarning]);

  const handleSetOpenAttachIssuesModal = (val: boolean) => {
    setOpenAttachIssuesModal(val);
  };

  const onAttachIssuesCallback = (data: {
    filesIssues?: IssueFileRow[];
    isSelected?: boolean;
    /** The selected issue statuses. `AttachIssues` holds this as a `string[]` (initialised `[]`). */
    selectedIssueType?: string[];
    selectedVersionType?: string;
  }) => {
    let filteredFilesIssues: IssueFileRow[] = [];
    const currentVersion = filesIssues.reduce(
      (acc: number, curr: IssueFileRow) => (acc > curr.fileVersionNumber! ? acc : curr.fileVersionNumber!),
      0,
    );

    if (data.selectedIssueType!.includes("open")) {
      filesIssues.forEach((file: IssueFileRow) => {
        if (file.status!.toLowerCase() == "open") {
          filteredFilesIssues.push(file);
        }
      });
    }
    if (data.selectedIssueType!.includes("in_review")) {
      filesIssues.forEach((file: IssueFileRow) => {
        if (file.status!.toLowerCase() == "in_review") {
          filteredFilesIssues.push(file);
        }
      });
    }
    if (data.selectedIssueType!.includes("closed")) {
      filesIssues.forEach((file: IssueFileRow) => {
        if (file.status!.toLowerCase() == "closed") {
          filteredFilesIssues.push(file);
        }
      });
    }
    if (data.selectedIssueType!.length == 0) {
      filteredFilesIssues = filesIssues;
    }

    if (data.selectedVersionType == "current_version") {
      filteredFilesIssues = filteredFilesIssues.filter(
        (file: IssueFileRow) => file.fileVersionNumber == currentVersion,
      );
    } else if (data.selectedVersionType == "previous_version") {
      filteredFilesIssues = filteredFilesIssues.filter(
        (file: IssueFileRow) => file.fileVersionNumber != currentVersion,
      );
    } else if (data.selectedVersionType == "") {
      filteredFilesIssues = [];
    }

    const selectedFilesToObj: Record<string, IssueFileRow> = { ...listOfSelectedFiles };
    filteredFilesIssues.map((file: IssueFileRow) => {
      selectedFilesToObj[file.issueVersionId!] = file;
    });
    setListOfSelectedFiles(selectedFilesToObj);
  };

  // handleCancelClickOnAttachFiles and handleAcceptClickOnAttachFiles are now in shared hook
  // as removeIssuesWithNoFiles and acceptFilesForIssues

  const handleCreateShortcut = async () => {
    // On this flow `shortCutData` is the shortcut request itself (the consume flow puts the selected
    // rows there instead), and "Create here" stays disabled until `handleFolderSelectionCallback`
    // reports a `folderType === 0` folder — every such row carries `spaceId` plus a `hierarchyId`
    // ("0" at the Space root), so the destination half of the guard below is defensive only.
    // `folderType` is no longer sent: `createFileShortcutSchema` is a strict `z.object` without it,
    // so validateRequest already stripped it before the service ever saw it.
    const shortcutRequest = Array.isArray(shortCutData) ? undefined : shortCutData;
    const { spaceId, hierarchyId } = selectedFolderData;
    if (!shortcutRequest?.transmittalId || !shortcutRequest.fileVersionIds || !spaceId || !hierarchyId) {
      // All four are required by `createFileShortcutSchema`, so before this guard existed an
      // incomplete request was still posted and came back rejected — closing the dialog and showing
      // the failure. Report it the same way rather than leaving the dialog open with no feedback.
      dispatch(showToastNotification({ content: t("Something went wrong"), type: TOAST_TYPE_ERROR }));
      handleFileSelectedCancelClick();
      return;
    }
    setSubmitButtonLoading(true);
    const res = await createFileShortcut({
      transmittalId: shortcutRequest.transmittalId,
      fileVersionIds: shortcutRequest.fileVersionIds,
      spaceId,
      parentId: hierarchyId,
    });
    handleFileSelectedCancelClick();
    if (!res.data.status) {
      dispatch(
        showToastNotification({
          // `errorMessage` is raw server copy, so it goes through `untranslated()` rather than `t()`.
          content: res.data.errorMessage ? untranslated(res.data.errorMessage) : t("Something went wrong"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: t("Shortcut created successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    }
    setSubmitButtonLoading(false);
  };

  /**
   * The consume request for the current destination folder, or `null` when no folder is selected —
   * "Consume here" stays disabled until `handleFolderSelectionCallback` sets one. On this flow
   * `shortCutData` holds the selected reference rows. `folderType` is no longer sent: although
   * `consumeExternalFileSchema` is a `looseObject` and so used to pass it through, the service
   * (fileOperations.utility.ts `consumeExternalFiles`) destructures only fileVersionIds/spaceId/
   * parentId/skipDuplicateCheck/transmittalData and never read it.
   */
  const buildConsumePayload = (): ConsumeFilesPayload | null => {
    const { spaceId, hierarchyId } = selectedFolderData;
    if (!spaceId || !hierarchyId) return null;
    return {
      fileVersionIds: (Array.isArray(shortCutData) ? shortCutData : []).map((row) => ({
        fileVersion: row.fileVersion,
        uploadNewVersion: false,
      })),
      spaceId,
      parentId: hierarchyId,
      skipDuplicateCheck: false,
      transmittalData: externalFolderData,
    };
  };

  const handleConsumeFiles = async (payload?: ConsumeFilesPayload) => {
    const consumePayload = payload ?? buildConsumePayload();
    if (!consumePayload) {
      // `spaceId`/`parentId` are required by `consumeExternalFileSchema`, so before this guard existed
      // the incomplete request was still posted and came back rejected — closing the dialog and
      // showing the failure. Report it the same way rather than silently doing nothing.
      dispatch(showToastNotification({ content: t("Something went wrong"), type: TOAST_TYPE_ERROR }));
      handleFileSelectedCancelClick();
      return;
    }
    setSubmitButtonLoading(true);
    const res = await consumeExternalFiles(consumePayload);
    if (res.data.status) {
      // `duplicatesFound` reaches the client through StandardResponse's index signature, so it
      // arrives as `unknown`; the endpoint's response schema declares it `z.number().optional()`.
      const duplicatesFound = typeof res.data.duplicatesFound === "number" ? res.data.duplicatesFound : 0;
      if (duplicatesFound) {
        dispatch(
          showToastNotification({
            content: t("Duplicate file(s) found so files were not consumed."),
            type: TOAST_TYPE_ERROR,
          }),
        );
        setDuplicatePrompt({ duplicatesFound, payload: consumePayload });
      } else {
        dispatch(
          showToastNotification({
            content: t("File(s) consumed successfully"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      }
    } else if (res.data.errorCode) {
      dispatch(
        showToastNotification({
          content: t("Some Error(s) occured. Please check Escrow files section."),
          type: TOAST_TYPE_WARNING,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          // `errorMessage` is raw server copy, so it goes through `untranslated()` rather than `t()`.
          content: res.data.errorMessage ? untranslated(res.data.errorMessage) : t("Something went wrong"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
    setSubmitButtonLoading(false);
    handleFileSelectedCancelClick();
  };

  const handleDuplicateClick = async (button: "uploadNewVersion" | "uploadNewFile") => {
    if (!duplicatePrompt) return;
    const { payload } = duplicatePrompt;
    setDuplicatePrompt(null);
    await handleConsumeFiles({
      ...payload,
      skipDuplicateCheck: true,
      fileVersionIds: payload.fileVersionIds.map((entry) => ({ ...entry, [button]: true })),
    });
  };

  function footer() {
    return (
      <div className="flex flex-row gap-6 py-4 px-6 justify-end">
        <ButtonComponent
          onClick={() => handleDuplicateClick("uploadNewVersion")}
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        >
          <Text>{t("Upload all as new versions")}</Text>
        </ButtonComponent>

        <ButtonComponent
          onClick={() => handleDuplicateClick("uploadNewFile")}
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        >
          <Text>{t("Upload all as new files")}</Text>
        </ButtonComponent>
      </div>
    );
  }

  function modelContent(duplicatesFound: number) {
    return (
      <div className="my-5">{`${duplicatesFound} files have duplicates at the selected location. How do you want to proceed?`}</div>
    );
  }

  function popUpContent() {
    return (
      <div className="flex flex-col p-6 gap-6 overflow-auto h-full">
        <div className="flex flex-col gap-6 p-4 border border-button_light rounded-lg">
          {!showNoSpaceWarning && (
            <>
              {(showViewToggle || allowInput) && (
                <div className="flex flex-row gap-4 items-center mx-1">
                  {allowInput && (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center">
                        <span>{inputLabel}</span>
                        <div className="ps-4">
                          <TextFieldComponent
                            type="text"
                            name="mergedFileName"
                            placeholder={t("Add name here")}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            slotProps={{
                              htmlInput: {
                                "data-testid": "addMergeFileName",
                              },
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {showViewToggle && (
                    <div className="flex items-center gap-2">
                      <ToggleSwitchComponent checked={!toggleFilesAsList} onChange={handleToggleFilesAsList} />
                      {toggleFilesAsList ? (
                        <Text className="text-sm">{t("Seeing files as a list")}</Text>
                      ) : (
                        <Text className="text-sm">{t("Seeing files as folder structure")}</Text>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {getContent()}
          {!showNoSpaceWarning && (
            <>
              {!createShortcut && (
                <div className="col-start-2 row-start-1">
                  <ButtonComponent
                    buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                    onClick={handleAddToSelection}
                    disabled={AddToListDisabled}
                    data-testid="addFilesToSelectionBelowBtn"
                  >
                    <div className="flex flex-row gap-2 items-center">
                      <Text>{t("Add to Selection Below")}</Text>
                    </div>
                  </ButtonComponent>
                </div>
              )}
            </>
          )}
        </div>
        {!showNoSpaceWarning && (
          <div className="flex justify-between overflow-auto max-h-[15.625rem] flex-shrink-0">
            {!createShortcut && (
              <div className="flex flex-col w-full border border-button_light rounded-lg">
                <Text className="flex p-4  w-full border-b border-button_light font-semibold text-base">
                  {t("Selection")}
                </Text>
                <div className="flex flex-col gap-4 p-4 w-full">
                  <div>
                    <ButtonComponent
                      buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                      disabled={Object.keys(listOfSelectedFiles).length === 0}
                      onClick={() => setListOfSelectedFiles({})}
                    >
                      <Text>{t("Remove All")}</Text>
                    </ButtonComponent>
                  </div>
                  <div className="flex gap-5 flex-row overflow-auto">
                    {Object.keys(listOfSelectedFiles).length === 0 ? (
                      <Text className="text-sm leading-[120% font-semibold text-yet_to_start_communication py-2">
                        {t("No File(s) Added")}
                      </Text>
                    ) : (
                      Object.keys(listOfSelectedFiles).map((file) => {
                        if (
                          [
                            "RESOURCE_TYPE_FILE",
                            "RESOURCE_TYPE_MERGED_FILE",
                            "RESOURCE_TYPE_ESTIMATE",
                            "RESOURCE_TYPE_EMBED",
                          ].includes(listOfSelectedFiles[file]!.resourceType!) ||
                          Object.hasOwn(listOfSelectedFiles[file]!, "dev")
                        ) {
                          return (
                            <div
                              key={file}
                              className="flex border border-stroke_primary gap-2 p-1.5 rounded items-center flex-shrink-0"
                            >
                              <div className="w-4 h-4 flex items-center justify-center">
                                {getFileIcon(listOfSelectedFiles[file]!.name || listOfSelectedFiles[file]!.fileName)}
                              </div>
                              <div className="text-sm leading-[120%]">
                                {listOfSelectedFiles[file]!.name || listOfSelectedFiles[file]!.fileName}
                              </div>
                              <IconComponent
                                name="Cancel"
                                onClick={() => handleRemoveOfSelectedFile(listOfSelectedFiles[file]!)}
                                width="1rem"
                                height="1rem"
                              />
                            </div>
                          );
                        }
                        return null;
                      })
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {errorMessage && (
          <div className="ps-4 pt-4 text-[var(--negative-communication)]">
            <span>{errorMessage}</span>
          </div>
        )}
      </div>
    );
  }

  function popUpFooter() {
    return (
      <div className="flex flex-row gap-6 py-4 px-6">
        {!createShortcut && !showNoSpaceWarning && (
          <ButtonComponent
            buttonType="gradientButton"
            onClick={handleFolderSelected ? handleFolderSelectedCallback : handleFileSelectedButtonClickCallback}
            disabled={disableAttachButton}
            loading={isLoadingCreateButton || isLoadingAddFiles}
            data-testid="attachSelectedBtn"
          >
            {attachSelectedBtnName || t("Attach")}
          </ButtonComponent>
        )}
        {createShortcut && !consumeFiles && (
          <ButtonComponent
            buttonType="gradientButton"
            onClick={handleCreateShortcut}
            disabled={disableCreateShortcutButton}
          >
            <Text>{t("Create here")}</Text>
          </ButtonComponent>
        )}
        {consumeFiles && (
          <ButtonComponent
            buttonType="gradientButton"
            onClick={() => handleConsumeFiles()}
            loading={submitButtonLoading}
            disabled={disableCreateShortcutButton}
          >
            <Text>{t("Consume here")}</Text>
          </ButtonComponent>
        )}
        <ButtonComponent onClick={handleFileSelectedCancelClick} data-testid="cancelSelectedFileBtn">
          <Text>{t("Cancel")}</Text>
        </ButtonComponent>
      </div>
    );
  }

  const getContent = () => {
    if (showNoSpaceWarning == null) {
      return (
        <div className="flex justify-center h-full flex-col gap-2 items-center">
          <CircularProgress />
        </div>
      );
    } else if (showNoSpaceWarning) {
      return (
        <Text className="flex justify-center h-full flex-col gap-2 items-center">
          <IconComponent name="InspectionFormNoData" width="18.75rem" height="18.75rem" />
          {t("No Spaces available in this project")}
        </Text>
      );
    } else {
      return (
        <div className={`flex border border-button_light rounded-lg h-[27.75rem] overflow-auto`}>
          {toggleFilesAsList ? (
            <FilesGrid
              initialShowAllVersions={false}
              spaceId={allowSwitchSpace ? null : switchSpaceId}
              projectId={projectId!}
              showTitle={false}
              showToolbar={{ views: true, group: false, upload: false, action: false, pinnedColumns: false }}
              propertiesPanel={false}
              disableMenuOptions
              excelExport={false}
              handleFileSelectionCallback={(rows: FilesGridPickerRow[]) =>
                handleFileSelectionCallback(rows.map(toPopupFileRow))
              }
              showOnlyMergedFile={showOnlyMergedFile}
              disableFiles={disabledFileVersionIds}
            />
          ) : (
            <FileManager
              projectId={projectId!}
              spaceId={switchSpaceId || ""}
              fileManagerAsPopup
              openThisSpaceByDefault={openThisSpaceByDefault}
              handleFileSelectionCallback={handleFileSelectionCallback as (files: SelectedFileItem[]) => void}
              handleFolderSelectionCallback={handleFolderSelectionCallback}
              allowFolderSelectionCallback={allowFolderSelectionCallback}
              hideIssues={hideIssues}
              hideTasks={hideTasks}
              hideReviews={hideReviews}
              hideAllTabs={hideAllTabs}
              hideEstimates={hideEstimates}
              hideSchedules={hideSchedules}
              hideContracts={hideContracts}
              hideMergeFiles={hideMergeFiles}
              hideExternalSpace={hideExternalSpace}
              useAttachedExternalVolume={useAttachedExternalVolume}
              hideClashTests={hideClashTests}
              // enablePersistence={false}
              createShortcut={createShortcut}
              disableFiles={disableFiles}
              enableFilesTypes={enableFilesTypes}
              hideShortcuts={hideShortcuts}
              hideUpload
              showOnlyMergedFile={showOnlyMergedFile}
              alreadySelected={preSelectedFiles}
              setSpaceId={setSpaceId}
              disableContextMenu={disableContextMenu}
              enableOnlyShareableResources={enableOnlyShareableResources}
              disableDragAndDropUpload={true}
              hideFilesWithoutVersion={hideFilesWithoutVersion}
            />
          )}
        </div>
      );
    }
  };
  return (
    <>
      <div>
        {openAttachIssuesModal && (
          <Box
            onClick={(e: { stopPropagation: () => void }) => {
              e.stopPropagation();
            }}
          >
            <AttachIssues
              handleSetOpenAttachIssuesModal={handleSetOpenAttachIssuesModal}
              onSuccess={onAttachIssuesCallback}
            />
          </Box>
        )}
        {issuesHavingNoFiles.length > 0 && (
          <Box
            onClick={(e: { stopPropagation: () => void }) => {
              e.stopPropagation();
            }}
          >
            <AttachFiles onCancelClick={removeIssuesWithNoFiles} onAcceptClick={acceptFilesForIssues} />
          </Box>
        )}
        <ModalStateDialogComponent
          modalPadding={0}
          headerTitle={
            <div className="flex flex-row ps-6 py-4 w-full justify-between items-center">
              <ToolTipComponent content={fileManagerHeading}>
                <span className="text-2xl font-semibold truncate max-w-[50%]" data-testid="fileManagerHeading">
                  {fileManagerHeading}
                </span>
              </ToolTipComponent>
              {!showNoSpaceWarning && !(showViewToggle && toggleFilesAsList) && (
                <SelectSpaces
                  disablePermissionCheck
                  projectId={projectId!}
                  workspaceId={switchSpaceId}
                  dataSource={spaceListDataSource}
                  isDisabled={
                    toggleFilesAsList ||
                    (PMPLAN && !CDEPLAN) ||
                    (!allowSwitchSpace && (spaceListDataSource?.length ?? 0) <= 1)
                  }
                  selectedSpaceCallback={handleSpaceSelect}
                  // @ts-expect-error -- the delete-space response callback is written against a `result` shape with `_id`/`spaceName`, but the endpoint's response declares `deletedSpaceName`/`spaceName`. The two disagree on the field name.
                  fetchSpaceListCallback={handleSpaceListFetch}
                />
              )}
            </div>
          }
          footerContent={popUpFooter()}
          modelContent={popUpContent()}
          isVisible
          width="90%"
          height="90%"
          onCancel={handleFileSelectedCancelClick}
        />
      </div>
      {duplicatePrompt && (
        <ModalStateDialogComponent
          headerTitle={t("Duplicates found")}
          footerContent={footer()}
          modelContent={modelContent(duplicatePrompt.duplicatesFound)}
          isVisible
          width="40%"
          onCancel={() => setDuplicatePrompt(null)}
        />
      )}
    </>
  );
}

export default FileManagerAsPopup;
